import { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { ResponsiveDataList } from '@core/components/ui';
import { BaseCard } from './BaseCard';
import { BaseTableActions } from './BaseTableActions';

/**
 * BaseTable Component
 *
 * Displays knowledge bases (general + dedicated) in a responsive table (desktop) or cards (mobile).
 * Uses ResponsiveDataList wrapper with TanStack Table.
 *
 * @param {Object} props
 * @param {Array} props.bases - Array of knowledge base objects (includes general base)
 * @param {Function} props.onEdit - Callback when editing a base
 * @param {Function} props.onDelete - Callback when deleting a base
 */
export function BaseTable({ bases, onEdit, onDelete }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Column definitions
  const columns = useMemo(() => [
    {
      accessorKey: 'name',
      header: () => t('knowledgeBases.table.name', 'Name'),
      cell: ({ row }) => {
        const base = row.original;
        const isGeneral = base.id === 'general';
        return (
          <div className="py-1 space-y-0.5">
            {/* Name with badge for general base */}
            <div className="flex items-center gap-2">
              <span className="text-default font-semibold break-words">
                {base.name || '-'}
              </span>
              {isGeneral && (
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                  style={{
                    border: '1px solid transparent',
                    background: 'linear-gradient(#fff, #fff) padding-box, linear-gradient(135deg, #ff9e1b, #ffb991, #c2b4e4) border-box',
                    color: '#4d2d1e'
                  }}
                >
                  <Icon icon="lucide:sparkles" className="size-3 mr-1 text-brand" />
                  {t('knowledgeBases.general.badge', 'General')}
                </span>
              )}
            </div>

            {/* Description */}
            {base.description && (
              <p className="text-subtle text-sm break-words line-clamp-2">
                {base.description}
              </p>
            )}
          </div>
        );
      },
      enableSorting: true,
      enableGlobalFilter: true,
      filterFn: (row, columnId, filterValue) => {
        const name = row.original.name?.toLowerCase() || '';
        const description = row.original.description?.toLowerCase() || '';
        const search = filterValue.toLowerCase();
        return name.includes(search) || description.includes(search);
      },
    },
    {
      id: 'segmentation',
      header: () => t('knowledgeBases.table.segmentation', 'Segmentation'),
      cell: ({ row }) => {
        const base = row.original;
        const isGeneral = base.id === 'general';

        // General base has no segmentation
        if (isGeneral) {
          return (
            <span className="text-subtle text-sm italic">
              {t('knowledgeBases.table.allUsers', 'All users')}
            </span>
          );
        }

        // Legacy base (no base_type/code)
        if (!base.base_type || !base.code) {
          return (
            <span className="text-subtle text-sm italic">
              {t('knowledgeBases.table.legacy', 'Legacy')}
            </span>
          );
        }

        // Show base_type (without code)
        const typeLabel = base.base_type === 'COMPANY'
          ? t('knowledgeBases.type.company', 'Company')
          : t('knowledgeBases.type.collectiveAgreement', 'Agreement');

        return (
          <span className="inline-flex items-center px-2.5 py-1 text-xs font-normal text-brand bg-brand/3 rounded-xl">
            {typeLabel}
          </span>
        );
      },
      enableSorting: true,
      enableGlobalFilter: false,
      size: 200,
      minSize: 150,
      maxSize: 250,
    },
    {
      id: 'counts',
      header: () => t('knowledgeBases.table.info', 'Content'),
      cell: ({ row }) => {
        const base = row.original;
        return (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium bg-brand/10 text-brand rounded-full">
              <Icon icon="lucide:file-text" className="size-3" />
              {base.file_count || 0}
            </span>
            <span className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium bg-brand/10 text-brand rounded-full">
              <Icon icon="lucide:message-circle-question" className="size-3" />
              {base.faq_count || 0}
            </span>
          </div>
        );
      },
      enableSorting: false,
      enableGlobalFilter: false,
      size: 150,
      minSize: 150,
      maxSize: 150,
    },
    {
      id: 'actions',
      header: () => '',
      cell: ({ row }) => {
        const base = row.original;
        const isGeneral = base.id === 'general';

        // Don't show actions for general base
        if (isGeneral) {
          return null;
        }

        return (
          <BaseTableActions
            base={base}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        );
      },
      enableSorting: false,
      enableGlobalFilter: false,
      size: 90,
      minSize: 90,
      maxSize: 90,
    },
  ], [t, onEdit, onDelete]);

  // Render mobile item
  const renderMobileItem = useCallback((row) => {
    const base = row.original;
    const isGeneral = base.id === 'general';

    // For general base, show a card without edit/delete
    if (isGeneral) {
      return (
        <div
          className="bg-app border border-border-default rounded-lg p-4 hover:bg-brand/5 transition-colors cursor-pointer"
          onClick={() => navigate('/knowledge-bases/general')}
        >
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-base font-semibold text-default">{base.name}</h3>
            <span
              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
              style={{
                border: '1px solid transparent',
                background: 'linear-gradient(#fff, #fff) padding-box, linear-gradient(135deg, #ff9e1b, #ffb991, #c2b4e4) border-box',
                color: '#4d2d1e'
              }}
            >
              <Icon icon="lucide:sparkles" className="size-3 mr-1 text-brand" />
              {t('knowledgeBases.general.badge', 'General')}
            </span>
          </div>
          {base.description && <p className="text-sm text-subtle mb-3">{base.description}</p>}
        </div>
      );
    }

    return (
      <BaseCard
        base={base}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    );
  }, [onEdit, onDelete, navigate, t]);

  // Custom row props for highlighting general base
  const getRowProps = useCallback((row) => {
    const isGeneral = row.original.id === 'general';
    return isGeneral
      ? {
          className: 'bg-brand/3 hover:bg-brand/5',
        }
      : {};
  }, []);

  return (
    <ResponsiveDataList
      data={bases}
      columns={columns}
      renderMobileItem={renderMobileItem}
      enableSorting
      enableFiltering
      searchPlaceholder={t('knowledgeBases.search', 'Search bases...')}
      onRowClick={(base) => navigate(`/knowledge-bases/${base.id}`)}
      getRowProps={getRowProps}
    />
  );
}

BaseTable.propTypes = {
  bases: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string,
      description: PropTypes.string,
    })
  ).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};
